import { isEnabledBySkills } from "./skills-Dj4T8Duc.js";
import { UserInputError } from "./errors-CEDWDXew.js";
import { getConstraintKeysToFilter, getConstraintParametersToInject } from "./constraint-helpers-C-VlK4t-.js";
import { isCatalogInfrastructureToolName, isOutsideCatalogToolName, isTopLevelToolName } from "./surfaces-B1_F0pzt.js";
import { isToolVisibleInMode, resolveDescription } from "./types-DGnYE-i7.js";
import { z } from "zod";

//#region src/tools/catalog-runtime/availability.ts
function getToolPlacement({ key, experimentalMode, useDefaultSurfacePolicy }) {
	if (useDefaultSurfacePolicy) return {
		isCatalogTool: !isOutsideCatalogToolName(key),
		isTopLevel: isTopLevelToolName(key, experimentalMode)
	};
	return {
		isCatalogTool: true,
		isTopLevel: true
	};
}
function hasRequiredCapabilities({ tool, context }) {
	if (!context.constraints.projectSlug || !context.constraints.projectCapabilities || !tool.requiredCapabilities?.length) return true;
	const caps = context.constraints.projectCapabilities;
	return tool.requiredCapabilities.every((cap) => caps[cap] === true);
}
function isAllowedBySkills({ tool, context }) {
	const grantedSkills = context.grantedSkills ? new Set(context.grantedSkills) : void 0;
	if (!grantedSkills) return false;
	return tool.skills.length > 0 && isEnabledBySkills(grantedSkills, tool.skills);
}
function isHiddenByConstraints({ key, context }) {
	return key === "find_organizations" && !!context.constraints.organizationSlug || key === "find_projects" && !!context.constraints.projectSlug || key === "create_project" && !!context.constraints.projectSlug;
}
function getFilteredInputSchema(tool, context) {
	const constraintKeysToFilter = new Set(getConstraintKeysToFilter(context.constraints, tool.inputSchema));
	return Object.fromEntries(Object.entries(tool.inputSchema).filter(([key]) => !constraintKeysToFilter.has(key)));
}
function injectConstraintParams(params, tool, context) {
	return {
		...params,
		...getConstraintParametersToInject(context.constraints, tool.inputSchema)
	};
}
function getAvailableTools({ tools, context, experimentalMode, useDefaultSurfacePolicy }) {
	const availableTools = [];
	for (const [key, tool] of Object.entries(tools)) {
		const placement = getToolPlacement({
			key,
			experimentalMode,
			useDefaultSurfacePolicy
		});
		if (!placement.isCatalogTool && !placement.isTopLevel) continue;
		if (!isToolVisibleInMode(tool, experimentalMode)) continue;
		if (!isAllowedBySkills({
			tool,
			context
		})) continue;
		if (isHiddenByConstraints({
			key,
			context
		})) continue;
		if (!hasRequiredCapabilities({
			tool,
			context
		})) continue;
		availableTools.push({
			key,
			tool,
			...placement
		});
	}
	return availableTools;
}
function getToolsForMcpRegistration({ tools, context, experimentalMode, useDefaultSurfacePolicy }) {
	const availableTools = getAvailableTools({
		tools,
		context,
		experimentalMode,
		useDefaultSurfacePolicy
	});
	return availableTools.filter(({ isTopLevel }) => isTopLevel);
}
function getSearchableTools({ tools, context, experimentalMode, useDefaultSurfacePolicy }) {
	return getAvailableTools({
		tools,
		context,
		experimentalMode,
		useDefaultSurfacePolicy
	}).filter(({ key, isCatalogTool }) => isCatalogTool && !isCatalogInfrastructureToolName(key));
}
function formatZodIssues(error) {
	return error.issues.map((issue) => {
		const path = issue.path.length > 0 ? issue.path.join(".") : "arguments";
		return `${path}: ${issue.message}`;
	}).join("; ");
}
async function executeToolHandler({ tool, params, context }) {
	const paramsWithConstraints = prepareToolParams({
		tool,
		params,
		context
	});
	return tool.handler(paramsWithConstraints, context);
}
function prepareToolParams({ tool, params, context }) {
	const filteredInputSchema = getFilteredInputSchema(tool, context);
	const schema = z.object(filteredInputSchema);
	const parsed = schema.safeParse(params);
	if (!parsed.success) throw new UserInputError(`Invalid arguments for ${tool.name}: ${formatZodIssues(parsed.error)}`);
	return injectConstraintParams(parsed.data, tool, context);
}
function resolveToolDescription(tool, context) {
	return resolveDescription(tool.description, context);
}

//#endregion
export { executeToolHandler, getAvailableTools, getFilteredInputSchema, getSearchableTools, getToolsForMcpRegistration, injectConstraintParams, prepareToolParams, resolveToolDescription };
//# sourceMappingURL=availability-zLnbaxcD.js.map